/**
 * GA4 Analytics module for Enchanted Gear Rentals.
 *
 * HOW TO ACTIVATE GA4:
 *   1. Open /app/frontend/.env
 *   2. Add the line: REACT_APP_GA4_MEASUREMENT_ID=G-XXXXXXXXXX
 *      (replace G-XXXXXXXXXX with your real Google Analytics 4 Measurement ID)
 *   3. Restart the frontend service: sudo supervisorctl restart frontend
 *
 * When REACT_APP_GA4_MEASUREMENT_ID is absent or empty:
 *   - No gtag.js script is loaded
 *   - No GA cookies are created
 *   - No events are sent to Google
 *   - No console errors are produced
 *
 * GA4 is ADDITIVE — the existing internal analytics.js system is unaffected.
 * Never send PII (names, emails, phones, addresses) to GA4.
 */

const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

/** True only when a non-empty, non-placeholder Measurement ID is configured. */
const GA4_ENABLED = !!GA4_ID && GA4_ID.trim().length > 0;

/**
 * Set of transaction IDs for which a purchase event has already been fired
 * this session — prevents duplicates from React re-renders or Stripe
 * double-callbacks.
 */
const _firedPurchases = new Set();

/** Internal — safe wrapper that does nothing if GA4 is not enabled. */
function _gtag(...args) {
  if (!GA4_ENABLED) return;
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag(...args);
  } catch (e) {
    // Never let GA4 errors break the application.
  }
}

/**
 * Load the GA4 script and initialize gtag once. Safe to call multiple times —
 * subsequent calls are no-ops.
 *
 * NOTE: The gtag.js <script> tag is now loaded statically from public/index.html
 * so that Google's tag verification can detect it in the initial HTML.
 * This function sets the initialization guard in case it is called before
 * the script in index.html has finished, and is kept for API compatibility.
 */
export function initGA4() {
  if (!GA4_ENABLED) return;
  if (typeof window === "undefined") return;
  // index.html already sets window.__egr_ga4_initialized and loads the script.
  // Mark it here too in case this function is called before the inline script runs.
  if (!window.__egr_ga4_initialized && typeof window.gtag === "function") {
    window.__egr_ga4_initialized = true;
  }
}

/**
 * Track a page view. Call this on every route change.
 *
 * @param {string} [path]  - URL path (defaults to window.location.pathname)
 * @param {string} [title] - Page title (defaults to document.title)
 */
export function trackPageView(path, title) {
  _gtag("event", "page_view", {
    page_title: title || (typeof document !== "undefined" ? document.title : ""),
    page_location: typeof window !== "undefined" ? window.location.href : "",
    page_path: path || (typeof window !== "undefined" ? window.location.pathname : ""),
  });
}

/**
 * Track a product/stroller page view (view_item).
 * Call when a stroller detail page finishes loading.
 *
 * @param {{ id: string, name: string, category: string, tier_1_3: number }} product
 */
export function trackViewItem(product) {
  if (!product) return;
  _gtag("event", "view_item", {
    currency: "USD",
    value: Number(product.tier_1_3) || 0,
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        item_category: product.category,
        price: Number(product.tier_1_3) || 0,
      },
    ],
  });
}

/**
 * Track checkout initiation (begin_checkout).
 * Call when the checkout page loads and a reservation is created.
 * NEVER include customer name, email, phone, or address.
 *
 * @param {{ value: number, items: Array, coupon?: string }} params
 */
export function trackBeginCheckout({ value, items, coupon } = {}) {
  _gtag("event", "begin_checkout", {
    currency: "USD",
    value: Number(value) || 0,
    ...(coupon ? { coupon } : {}),
    items: (items || []).map((i) => ({
      item_id: i.product_id || i.id || "",
      item_name: i.name || i.product_name || "",
      quantity: Number(i.quantity) || 1,
      price: Number(i.price_per_day || i.price || i.subtotal) || 0,
    })),
  });
}

/**
 * Track a completed purchase (purchase).
 * Call ONLY after the backend confirms payment success.
 * Prevents duplicate events across re-renders, back/forward, and callbacks.
 * NEVER include customer name, email, phone, or address.
 *
 * @param {{
 *   transactionId: string,
 *   value: number,
 *   items?: Array,
 *   coupon?: string,
 *   affiliationCode?: string
 * }} params
 */
export function trackPurchase({ transactionId, value, items, coupon, affiliationCode } = {}) {
  if (!transactionId) return;

  // In-memory dedup (survives React re-renders)
  if (_firedPurchases.has(transactionId)) return;
  _firedPurchases.add(transactionId);

  // sessionStorage dedup (survives Stripe redirect back/forward navigation)
  const storageKey = `_egr_ga4_purchase_${transactionId}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, "1");
  } catch (_) {
    // sessionStorage blocked — in-memory dedup above is still active.
  }

  _gtag("event", "purchase", {
    transaction_id: transactionId,
    currency: "USD",
    value: Number(value) || 0,
    ...(coupon ? { coupon } : {}),
    ...(affiliationCode ? { affiliation: affiliationCode } : {}),
    items: (items || []).map((i) => ({
      item_id: i.product_id || i.id || "",
      item_name: i.name || i.product_name || "",
      quantity: Number(i.quantity) || 1,
      price: Number(i.price_per_day || i.price || i.subtotal) || 0,
    })),
  });
}

/** @returns {boolean} Whether GA4 is configured and active. */
export function isGA4Enabled() {
  return GA4_ENABLED;
}
